import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getYearsFromRange } from '../../../../../../core/utils/year.utils';
import { CompanyResults } from '../../../../../../generated/model/companyResults';
import { RangeOflong } from '../../../../../../generated/model/rangeOflong';

const formatCompanyName = (t: TFunction, name?: string) => {
  return name && name !== '' ? name : t('companies:unknown');
};

interface OwnProps {
  tpPliLabel?: string;
  results?: CompanyResults[];
  resultsTp?: CompanyResults;
  yearRange: RangeOflong;
}

const TpPliTable: React.FC<OwnProps> = ({ tpPliLabel, results, resultsTp, yearRange }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const yearColumns = getYearsFromRange(yearRange);

  const combinedResults: CompanyResults[] = [];
  if (resultsTp) {
    combinedResults.push(resultsTp);
  }
  if (results) {
    combinedResults.push(...results);
  }
  const yearColWidth = yearColumns.length > 0 ? 50 / yearColumns.length + '%' : 'auto';

  return (
    <TableContainer component={Paper} className={styles.table}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={styles.headerTitle} colSpan={3 + yearColumns.length}>
              {tpPliLabel}
            </TableCell>
          </TableRow>
          <TableRow className={styles.header}>
            <TableCell>{t('benchmark:benchmarkResults.tables.tpPli.number')}</TableCell>
            <TableCell>{t('benchmark:benchmarkResults.tables.tpPli.companyName')}</TableCell>
            <TableCell style={{ width: 180 }}>
              {t('benchmark:benchmarkResults.tables.tpPli.notes')}
            </TableCell>
            <TableCell>
              {t('benchmark:benchmarkResults.tables.tpPli.companyWeightedAverage')}
            </TableCell>
            <TableCell>
              {t('benchmark:benchmarkResults.tables.statistic.numberOfObservations')}
            </TableCell>
            {yearColumns.map((yearColumn) => (
              <TableCell key={yearColumn} style={{ width: yearColWidth }}>
                {yearColumn}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {combinedResults.map((companyResult, i) => (
            <TableRow key={i} className={clsx(resultsTp && i === 0 && 'testedParty')}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{formatCompanyName(t, companyResult.companyName)}</TableCell>
              <TableCell>{companyResult.notes}</TableCell>
              <TableCell>{companyResult.weightedAverage}</TableCell>
              <TableCell>{companyResult.numberOfObservations}</TableCell>
              {yearColumns.map((yearColumn) => {
                const yearResult = companyResult.companyYearResults?.find(
                  (yr) => yr.year === yearColumn,
                );
                if (yearResult) {
                  return <TableCell key={yearColumn}>{yearResult.value}</TableCell>;
                } else {
                  return <TableCell key={yearColumn}></TableCell>;
                }
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  table: {
    borderRadius: 0,

    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: 'rgb(250,250,250)',
    },

    '& .MuiTableCell-body': {
      borderBottom: '1px solid rgb(199 199 199)',
      borderRight: '1px solid rgb(199 199 199)',
    },

    '& .testedParty': {
      backgroundColor: 'rgb(242,242,242)',
    },

    '& .MuiTable-root': {
      borderCollapse: 'unset',
      borderRadius: '0',
    },
    marginTop: theme.spacing(6),
    maxHeight: 600,
  },

  tableRoot: {
    '& .MuiTab-root': {
      borderCollapse: 'unset',
    },
  },

  header: {
    '& .MuiTableCell-head': {
      color: theme.palette.secondary.light,
      fontWeight: 'bold',
      backgroundColor: 'rgb(235,235,235)',
      borderTop: '1px solid rgb(199 199 199)',
      borderBottom: '1px solid rgb(199 199 199)',
      borderRight: '1px solid rgb(199 199 199)',
    },

    '&.MuiTableCell-stickyHeader': {},
  },

  headerTitle: {
    backgroundColor: theme.palette.background.paper,
    height: '60%',
    fontWeight: 'bold',
    color: theme.palette.primary.dark,
    borderLeftColor: '#0094c7',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    borderBottom: 'unset',
    lineHeight: '1em',
  },
}));

export default TpPliTable;
